/**
 * Custom hooks for Container Notes
 * CRUD operations for container notes (internal and customer types)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api-client';

// Types
export interface ContainerNote {
    note_id: string;
    container_id: string;
    note_type: 'internal' | 'customer';
    content: string;
    created_at: string;
    created_by_user_id: string;
    created_by_name?: string;
    updated_at?: string;
}

export interface ContainerNotesResponse {
    internal_notes: ContainerNote[];
    customer_notes: ContainerNote[];
    internal_count: number;
    customer_count: number;
}

interface CreateNoteData {
    note_type: 'internal' | 'customer';
    content: string;
}

interface UpdateNoteData {
    content: string;
}

// Query keys
const CONTAINER_NOTES_KEYS = {
    all: ['container-notes'] as const,
    byContainer: (containerId: string) => [...CONTAINER_NOTES_KEYS.all, containerId] as const,
};

/**
 * Hook to fetch all notes for a container
 */
export function useContainerNotes(containerId: string | undefined) {
    return useQuery({
        queryKey: CONTAINER_NOTES_KEYS.byContainer(containerId || ''),
        queryFn: async (): Promise<ContainerNotesResponse> => {
            return await apiGet<ContainerNotesResponse>(`/containers/${containerId}/notes`);
        },
        enabled: !!containerId,
    });
}

/**
 * Hook to create a new note
 */
export function useCreateContainerNote(containerId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateNoteData): Promise<ContainerNote> => {
            return await apiPost<ContainerNote>(`/containers/${containerId}/notes`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONTAINER_NOTES_KEYS.byContainer(containerId) });
        },
    });
}

/**
 * Hook to update a note
 */
export function useUpdateContainerNote(containerId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ noteId, data }: { noteId: string; data: UpdateNoteData }): Promise<ContainerNote> => {
            return await apiPatch<ContainerNote>(`/containers/${containerId}/notes/${noteId}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONTAINER_NOTES_KEYS.byContainer(containerId) });
        },
    });
}

/**
 * Hook to delete a note
 */
export function useDeleteContainerNote(containerId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (noteId: string): Promise<void> => {
            return await apiDelete<void>(`/containers/${containerId}/notes/${noteId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONTAINER_NOTES_KEYS.byContainer(containerId) });
        },
    });
}
